import type { ButtonVariant, ButtonHoverStyle, ButtonTheme } from '../types'

export interface VariantClassesOptions {
    variant: ButtonVariant
    theme: ButtonTheme
    disabled: boolean
}

/**
 * Get the base style classes for a button variant
 */
export function getVariantClasses(options: VariantClassesOptions): Record<string, boolean> {
    const { variant, theme, disabled } = options

    if (disabled) {
        return getDisabledClasses({ variant, theme })
    }

    if (theme === 'light') {
        return {
            'bg-se-dark text-se-white': variant === 'primary',
            'bg-se-white ring-1 ring-se-gray-300': variant === 'secondary',
            'border-1 border-solid border-se-gray-300': variant === 'outline',
            'bg-se-danger text-se-white': variant === 'danger',
        }
    }

    return {
        'bg-se-white': variant === 'primary',
        'bg-se-white ring-1 ring-se-gray-300': variant === 'secondary',
        'text-se-white border-se-white border-1 border-solid': variant === 'outline',
        'text-se-white': variant === 'ghost',
        'bg-se-danger text-se-white': variant === 'danger',
    }
}

/**
 * Get disabled state classes
 */
function getDisabledClasses(options: { variant: ButtonVariant; theme: ButtonTheme }): Record<string, boolean> {
    const { variant, theme } = options

    if (theme === 'light') {
        return {
            'bg-se-gray-700 text-se-gray-300': variant === 'primary',
            'text-se-gray-700': variant === 'ghost',
            'text-se-gray-500 bg-se-gray-100 border-1 border-solid border-se-gray-300': variant === 'outline',
            'text-se-gray-500 bg-se-gray-100 ring-1 ring-se-gray-300': variant === 'secondary',
            'bg-se-danger-light text-se-white': variant === 'danger',
        }
    }

    return {
        'bg-se-gray-300 text-se-gray-700': variant === 'primary',
        'text-se-gray-300': variant === 'ghost' || variant === 'outline',
        'border-1 border-solid border-se-gray-300': variant === 'outline',
        'text-se-gray-300 ring-1 ring-se-gray-300': variant === 'secondary',
        'bg-se-danger-light text-se-white': variant === 'danger',
    }
}

export interface HoverClassesOptions {
    variant: ButtonVariant
    hoverStyle: ButtonHoverStyle
    theme: ButtonTheme
    noHover: boolean
}

/**
 * Get transition/text hover classes (always applied)
 */
export function getTransitionClasses(options: HoverClassesOptions): string[] {
    const { variant, hoverStyle, theme, noHover } = options
    const classes: string[] = ['se-transition-all']

    if (noHover) return classes

    if (theme === 'light') {
        if (variant === 'primary' && hoverStyle === 'vibrant') {
            classes.push('hover:text-se-dark')
        }
        if (variant === 'ghost' && hoverStyle === 'vibrant') {
            classes.push('group-hover/ui-button:text-se-white', 'group-hover/buttons-container:text-se-white', 'hover:text-se-white')
        }
    }

    if (theme === 'dark') {
        if (variant === 'primary' && hoverStyle === 'vibrant') {
            classes.push('hover:text-se-dark')
        }
        if (variant === 'ghost' && hoverStyle === 'vibrant') {
            classes.push('group-hover/ui-button:text-se-dark', 'group-hover/buttons-container:text-se-dark', 'hover:text-se-dark')
        }
    }

    return classes
}

/**
 * Get background hover classes (excluded for inline buttons)
 */
export function getBackgroundHoverClasses(options: HoverClassesOptions): string[] {
    const { variant, hoverStyle, theme, noHover } = options
    const classes: string[] = []

    if (noHover) return classes

    if (theme === 'light') {
        if (variant === 'primary' && hoverStyle === 'vibrant') {
            classes.push('hover:bg-se-primary')
        }
        if (variant === 'ghost' && hoverStyle === 'vibrant') {
            classes.push('group-hover/ui-button:bg-se-dark', 'group-hover/buttons-container:bg-se-dark', 'hover:bg-se-dark')
        }
        if (variant === 'ghost' && hoverStyle === 'highlight') {
            classes.push('group-hover/ui-button:bg-se-gray-100', 'group-hover/buttons-container:bg-se-gray-100', 'hover:bg-se-gray-100')
        }
        if (variant === 'outline') {
            if (hoverStyle === 'vibrant') {
                classes.push('hover:bg-se-dark', 'hover:text-se-white')
            } else {
                classes.push('hover:bg-gray-100')
            }
        }
        if (variant === 'secondary') {
            classes.push('hover:bg-se-gray-100')
        }
        if (variant === 'danger') {
            classes.push('hover:bg-se-danger-dark')
        }
    }

    if (theme === 'dark') {
        if (variant === 'primary' && hoverStyle === 'vibrant') {
            classes.push('hover:bg-se-primary')
        }
        if (variant === 'ghost' && hoverStyle === 'vibrant') {
            classes.push('group-hover/ui-button:bg-se-white', 'group-hover/buttons-container:bg-se-white', 'hover:bg-se-white')
        }
        if (variant === 'ghost' && hoverStyle === 'highlight') {
            classes.push('group-hover/ui-button:bg-white/10', 'group-hover/buttons-container:bg-white/10', 'hover:bg-white/10')
        }
        if (variant === 'secondary') {
            classes.push('hover:bg-se-gray-100')
        }
        if (variant === 'danger') {
            classes.push('hover:bg-se-danger-dark')
        }
    }

    return classes
}

/**
 * Get arrow icon classes based on variant and theme
 */
export function getArrowClasses(options: { variant: ButtonVariant; hoverStyle: ButtonHoverStyle; theme: ButtonTheme }): string {
    const { variant, hoverStyle, theme } = options
    const base = 'w-3.5 h-3.5 -rotate-45 group-hover/ui-button:rotate-0 group-hover/buttons-container:rotate-0 se-transition-all'

    if (variant === 'primary' && theme === 'light') {
        return `${base} text-se-primary group-hover/ui-button:text-se-dark group-hover/buttons-container:text-se-dark`
    }
    if (variant === 'primary' && theme === 'dark') {
        return `${base} text-se-dark`
    }
    if (variant === 'ghost' && hoverStyle === 'vibrant') {
        return `${base} group-hover/ui-button:text-se-primary group-hover/buttons-container:text-se-primary`
    }
    if (variant === 'danger') {
        return `${base} text-se-danger-light`
    }

    return base
}

export interface PressedClassesOptions {
    variant: ButtonVariant
    theme: ButtonTheme
}

/**
 * Get pressed/toggled state classes for each variant.
 * These classes are applied when a button is toggled on or physically pressed.
 */
export function getPressedClasses(options: PressedClassesOptions): Record<string, boolean> {
    const { variant, theme } = options

    if (theme === 'light') {
        return {
            'bg-se-primary text-se-dark': variant === 'primary',
            'bg-se-dark text-se-white ring-se-dark ring-1': variant === 'secondary',
            'bg-se-dark text-se-white border-se-dark border-1 border-solid': variant === 'outline',
            'bg-se-gray-200': variant === 'ghost',
            'bg-se-danger-dark': variant === 'danger',
        }
    }

    return {
        'bg-se-primary text-se-dark': variant === 'primary',
        'bg-se-white text-se-dark ring-se-white ring-1': variant === 'secondary',
        'bg-se-white text-se-dark border-se-white border-1 border-solid': variant === 'outline',
        'bg-se-gray-700': variant === 'ghost',
        'bg-se-danger-dark': variant === 'danger',
    }
}

/**
 * Get CSS :active pseudo-class pressed styles for regular (non-toggle) buttons
 */
export function getActivePressedClasses(options: PressedClassesOptions): string[] {
    const { variant, theme } = options
    const classes: string[] = []

    if (theme === 'light') {
        if (variant === 'primary') {
            classes.push('active:bg-se-primary', 'active:text-se-dark')
        }
        if (variant === 'secondary') {
            classes.push('active:bg-se-dark', 'active:text-se-white', 'active:ring-se-dark', 'active:ring-1')
        }
        if (variant === 'ghost') {
            classes.push('active:bg-se-gray-200')
        }
        if (variant === 'outline') {
            classes.push('active:bg-se-dark', 'active:text-se-white', 'active:border-se-dark')
        }
        if (variant === 'danger') {
            classes.push('active:bg-se-danger-dark')
        }
    }

    if (theme === 'dark') {
        if (variant === 'primary') {
            classes.push('active:bg-se-primary', 'active:text-se-dark')
        }
        if (variant === 'secondary') {
            classes.push('active:bg-se-white', 'active:text-se-dark', 'active:ring-se-white', 'active:ring-1')
        }
        if (variant === 'ghost') {
            classes.push('active:bg-se-gray-700')
        }
        if (variant === 'outline') {
            classes.push('active:bg-se-white', 'active:text-se-dark', 'active:border-se-white')
        }
        if (variant === 'danger') {
            classes.push('active:bg-se-danger-dark')
        }
    }

    return classes
}

/**
 * Compute the effective hover style based on variant
 */
export function getEffectiveHoverStyle(hoverStyle: ButtonHoverStyle | undefined, variant: ButtonVariant): ButtonHoverStyle {
    return hoverStyle ?? (variant === 'primary' || variant === 'secondary' ? 'vibrant' : 'subtle')
}
